<script setup lang="ts">
import { ExclamationCircleIcon } from '@heroicons/vue/24/outline'
import { useField } from 'vee-validate'
import { computed, useAttrs, useTemplateRef, watch } from 'vue'

import TooltipApplier from '../overlays/TooltipApplier.vue'

const props = defineProps<{
  disabled?: boolean
  errorMessage?: string
  inCreateContext?: boolean
  labelText: string
  name?: string
  placeholder?: string
  required?: boolean
  status?: 'error' | 'success' | undefined
  testid?: string
}>()

const { errorMessage: vErrorMessage, value: vValue } = useField<string | undefined>(
  () => {
    return props.name ?? ''
  },
  undefined,
  {
    validateOnValueUpdate: false,
  },
)

const model = defineModel<string | undefined>({
  default: '',
  get: (value) => {
    return props.name ? vValue.value : value
  },
  set: (value) => {
    if (props.name) {
      vValue.value = value
    }

    return value
  },
})

watch(
  () => {
    return vValue.value
  },
  (newValue) => {
    console.debug('newValue', newValue)
    model.value = newValue
  },
)

const displayError = computed(() => {
  return props.errorMessage ?? vErrorMessage.value
})

const computedStatus = computed<typeof props.status>(() => {
  if (displayError.value) {
    return 'error'
  }

  return props.status
})

const input = useTemplateRef<HTMLInputElement>('input')
const onWrapperFocus = (): void => {
  input.value?.focus()
}

const attributesUse = useAttrs()
const { class: classAttr, ...attrs } = attributesUse
</script>

<template>
  <div
    class="relative rounded-lg border border-black-200 px-5 py-3 text-black-400"
    :class="[
      classAttr,
      !!displayError ? 'pr-9' : 'pr-5',
      computedStatus === 'error'
        ? 'border-radical_red-500 text-radical_red-500 focus:border-radical_red-500'
        : '',
      computedStatus === 'success' ? 'border-keppel-500 focus:border-keppel-500' : '',
      disabled
        ? 'cursor-not-allowed border-black-100 bg-black-100'
        : computedStatus === undefined
          ? 'cursor-text hover:border-lochmara-600'
          : 'cursor-text',
    ]"
    @focus="onWrapperFocus"
  >
    <label class="flex flex-col text-xs font-medium">
      {{ labelText }} {{ required ? '*' : '' }}
      <input
        v-bind="attrs"
        ref="input"
        v-model="model"
        class="mt-1.5 border-none bg-[inherit] p-0 text-sm text-black-900 placeholder:text-black-400 focus:ring-0"
        :class="[disabled ? 'cursor-not-allowed bg-black-100' : 'cursor-text']"
        :data-testid="props.testid"
        :has-error="!!displayError || !!vErrorMessage"
        max="2100-12-31"
        min="1900-01-01"
        :placeholder="placeholder || ''"
        type="date"
      />
    </label>
    <div class="absolute bottom-3 right-5">
      <TooltipApplier
        v-if="!!displayError"
        :align="inCreateContext ? 'right' : 'center'"
        is-red
        :label="displayError"
      >
        <ExclamationCircleIcon class="h-4 w-4 text-radical_red-500" />
      </TooltipApplier>
    </div>
  </div>
</template>

<style scoped>
/* TODO: use tailwind appearance-none */
input[type='date']::-webkit-calendar-picker-indicator {
  display: none;
  -webkit-appearance: none;
}
</style>
